import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  Layout,
  Text,
  Badge,
  Button,
  DataTable,
  BlockStack,
  InlineStack,
  Divider,
  SkeletonBodyText,
  Banner,
  Box,
  Modal,
  TextField,
  Select,
  Pagination,
  Checkbox,
} from '@shopify/polaris';
import { salesAgentsApi, clinicsApi } from '@/services';
import type { AgentClinicAssignment, AgentCommission } from '@/services/salesAgents';
import type { Clinic } from '@/types';
import { orderNumber } from '@/utils/orderNumber';
import SearchableSelect from '@/components/SearchableSelect';

export default function SalesAgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [commissionPage, setCommissionPage] = useState(1);
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    commissionRate: 0,
    salesCommissionRate: 0,
    status: 'active',
  });
  const [newAssignment, setNewAssignment] = useState({
    clinicId: '',
    assignmentType: '0',
  });

  // Fetch agent details
  const { data: agent, isLoading, error } = useQuery({
    queryKey: ['salesAgent', id],
    queryFn: () => salesAgentsApi.getById(id!),
    enabled: !!id,
  });

  // Fetch agent's clinics
  const { data: clinics } = useQuery({
    queryKey: ['salesAgentClinics', id],
    queryFn: () => salesAgentsApi.getClinics(id!),
    enabled: !!id,
  });

  // Fetch agent's commissions
  const { data: commissions } = useQuery({
    queryKey: ['salesAgentCommissions', id, commissionPage],
    queryFn: () => salesAgentsApi.getCommissions(id!, { page: commissionPage, limit: 10 }),
    enabled: !!id,
  });

  // Clinic picker for the assignment modal. Searches on the server, then drops any
  // clinic already assigned to this agent. The previous version capped the fetch at
  // 100 of 842 clinics and filtered client-side, so most were never offered.
  const [assignClinicName, setAssignClinicName] = useState('');
  const searchUnassignedClinics = useCallback(
    async (query: string) => {
      const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
      const assigned = new Set(
        (clinics || []).map((c: AgentClinicAssignment) => c.clinicId),
      );
      return res.data
        .filter((c: Clinic) => !assigned.has(c.id))
        .map((c: Clinic) => ({ label: c.name, value: c.id, detail: c.email || undefined }));
    },
    [clinics],
  );

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: typeof editData) => salesAgentsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesAgent', id] });
      setShowEditModal(false);
    },
  });

  // Assign to clinic mutation
  const assignMutation = useMutation({
    mutationFn: (data: { clinicId: string; assignmentType: number }) =>
      salesAgentsApi.assignToClinic(id!, data.clinicId, data.assignmentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesAgentClinics', id] });
      setShowAssignModal(false);
      setNewAssignment({ clinicId: '', assignmentType: '0' });
    },
  });

  // Remove from clinic mutation
  const removeMutation = useMutation({
    mutationFn: (clinicId: string) => salesAgentsApi.removeFromClinic(id!, clinicId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesAgentClinics', id] });
    },
  });

  // Mark commissions paid mutation
  const markPaidMutation = useMutation({
    mutationFn: (ids: string[]) => salesAgentsApi.markCommissionsPaid(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesAgentCommissions', id] });
      setSelectedCommissions([]);
    },
  });

  const handleEdit = useCallback(() => {
    if (agent) {
      setEditData({
        name: agent.name,
        email: agent.email,
        phone: agent.phone || '',
        commissionRate: agent.commissionRate,
        salesCommissionRate: agent.salesCommissionRate ?? 0,
        status: agent.status,
      });
      setShowEditModal(true);
    }
  }, [agent]);

  const handleSaveEdit = useCallback(() => {
    updateMutation.mutate(editData);
  }, [editData, updateMutation]);

  const handleAssignClinic = useCallback(() => {
    if (!newAssignment.clinicId) return;
    assignMutation.mutate({
      clinicId: newAssignment.clinicId,
      assignmentType: parseInt(newAssignment.assignmentType),
    });
  }, [newAssignment, assignMutation]);

  const handleToggleCommission = useCallback((commissionId: string) => {
    setSelectedCommissions((prev) =>
      prev.includes(commissionId)
        ? prev.filter((id) => id !== commissionId)
        : [...prev, commissionId]
    );
  }, []);

  const handleMarkPaid = useCallback(() => {
    if (selectedCommissions.length > 0) {
      markPaidMutation.mutate(selectedCommissions);
    }
  }, [selectedCommissions, markPaidMutation]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAssignmentTypeLabel = (type: number) => {
    switch (type) {
      case 0:
        return 'Primary';
      case 1:
        return 'Secondary';
      case 2:
        return 'Split';
      default:
        return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <Page title="Sales Agent">
        <Card>
          <SkeletonBodyText lines={10} />
        </Card>
      </Page>
    );
  }

  if (error || !agent) {
    return (
      <Page title="Sales Agent">
        <Banner tone="critical">Agent not found or error loading details.</Banner>
      </Page>
    );
  }

  const clinicRows = (clinics || []).map((assignment: AgentClinicAssignment) => [
    <Button key={assignment.clinicId} variant="plain" onClick={() => navigate(`/clinics/${assignment.clinicId}`)}>
      {assignment.clinic.name}
    </Button>,
    getAssignmentTypeLabel(assignment.assignmentType),
    assignment.splitPercent ? `${(Number(assignment.splitPercent) * 100).toFixed(0)}%` : 'N/A',
    formatDate(assignment.startDate),
    <Badge key={`status-${assignment.id}`} tone={assignment.status === 'active' ? 'success' : undefined}>
      {assignment.status}
    </Badge>,
    <Button
      key={`remove-${assignment.id}`}
      variant="plain"
      tone="critical"
      onClick={() => removeMutation.mutate(assignment.clinicId)}
      loading={removeMutation.isPending}
    >
      Remove
    </Button>,
  ]);

  const commissionRows = (commissions?.data || []).map((commission: AgentCommission) => {
    const baseAmount = commission.commissionType === 'sale'
      ? (commission.orderAmount ?? 0)
      : (commission.rentalAmount ?? 0);
    const orderRef = commission.commissionType === 'sale'
      ? (commission.shopifyOrderId || 'N/A')
      : ((commission.rental && orderNumber(commission.rental)) || 'N/A');
    return [
      <Checkbox
        key={`check-${commission.id}`}
        label=""
        labelHidden
        checked={selectedCommissions.includes(commission.id)}
        onChange={() => handleToggleCommission(commission.id)}
        disabled={commission.status === 'paid'}
      />,
      orderRef,
      <Badge key={`type-${commission.id}`} tone={commission.commissionType === 'sale' ? 'info' : undefined}>
        {commission.commissionType}
      </Badge>,
      commission.clinic?.name || 'Direct',
      formatCurrency(baseAmount),
      `${(Number(commission.commissionRate) * 100).toFixed(1)}%`,
      formatCurrency(commission.commissionAmount),
      <Badge
        key={`status-${commission.id}`}
        tone={commission.status === 'paid' ? 'success' : 'warning'}
      >
        {commission.status}
      </Badge>,
      formatDate(commission.createdAt),
    ];
  });

  // Calculate totals
  const pendingCommissions = (commissions?.data || [])
    .filter((c: AgentCommission) => c.status === 'pending')
    .reduce((sum: number, c: AgentCommission) => sum + Number(c.commissionAmount), 0);

  return (
    <Page
      title={agent.name}
      backAction={{ content: 'Sales Agents', onAction: () => navigate('/sales') }}
      secondaryActions={[
        { content: 'Edit', onAction: handleEdit },
      ]}
    >
      <Layout>
        {/* Agent Info */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Agent Details</Text>
              <Divider />
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Email</Text>
                  <Text as="span">{agent.email}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Phone</Text>
                  <Text as="span">{agent.phone || 'N/A'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Rental Commission</Text>
                  <Text as="span">{(Number(agent.commissionRate) * 100).toFixed(1)}%</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Sales Commission</Text>
                  <Text as="span">
                    {Number(agent.salesCommissionRate) > 0
                      ? `${(Number(agent.salesCommissionRate) * 100).toFixed(1)}%`
                      : 'Disabled'}
                  </Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Status</Text>
                  <Badge tone={agent.status === 'active' ? 'success' : undefined}>
                    {agent.status}
                  </Badge>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Joined</Text>
                  <Text as="span">{formatDate(agent.createdAt)}</Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>

          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Commission Summary</Text>
                <Divider />
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Pending</Text>
                    <Text as="span" fontWeight="bold">{formatCurrency(pendingCommissions)}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Assigned Clinics</Text>
                    <Text as="span">{clinics?.length || 0}</Text>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>
          </Box>
        </Layout.Section>

        {/* Assigned Clinics */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">Assigned Clinics</Text>
                <Button onClick={() => setShowAssignModal(true)}>Assign Clinic</Button>
              </InlineStack>
              <Divider />
              {clinicRows.length === 0 ? (
                <Text as="p" tone="subdued">No clinics assigned yet.</Text>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Clinic', 'Type', 'Split', 'Since', 'Status', 'Action']}
                  rows={clinicRows}
                />
              )}
            </BlockStack>
          </Card>

          {/* Commissions */}
          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Commissions</Text>
                  {selectedCommissions.length > 0 && (
                    <Button
                      variant="primary"
                      onClick={handleMarkPaid}
                      loading={markPaidMutation.isPending}
                    >
                      Mark {String(selectedCommissions.length)} as Paid
                    </Button>
                  )}
                </InlineStack>
                <Divider />
                {commissionRows.length === 0 ? (
                  <Text as="p" tone="subdued">No commissions yet.</Text>
                ) : (
                  <>
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'numeric', 'text', 'text']}
                      headings={['', 'Order', 'Type', 'Clinic', 'Amount', 'Rate', 'Commission', 'Status', 'Date']}
                      rows={commissionRows}
                    />
                    {commissions && commissions.meta.totalPages > 1 && (
                      <InlineStack align="center">
                        <Pagination
                          hasPrevious={commissionPage > 1}
                          hasNext={commissionPage < commissions.meta.totalPages}
                          onPrevious={() => setCommissionPage((p) => p - 1)}
                          onNext={() => setCommissionPage((p) => p + 1)}
                        />
                      </InlineStack>
                    )}
                  </>
                )}
              </BlockStack>
            </Card>
          </Box>
        </Layout.Section>
      </Layout>

      {/* Edit Modal */}
      <Modal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Sales Agent"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveEdit,
          loading: updateMutation.isPending,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowEditModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Name"
              value={editData.name}
              onChange={(value) => setEditData((prev) => ({ ...prev, name: value }))}
              autoComplete="off"
            />
            <TextField
              label="Email"
              type="email"
              value={editData.email}
              onChange={(value) => setEditData((prev) => ({ ...prev, email: value }))}
              autoComplete="off"
            />
            <TextField
              label="Phone"
              type="tel"
              value={editData.phone}
              onChange={(value) => setEditData((prev) => ({ ...prev, phone: value }))}
              autoComplete="off"
            />
            <TextField
              label="Rental Commission Rate (%)"
              type="number"
              value={String(editData.commissionRate * 100)}
              onChange={(value) => setEditData((prev) => ({ ...prev, commissionRate: parseFloat(value) / 100 || 0 }))}
              autoComplete="off"
              suffix="%"
              helpText="Commission earned on rental orders."
            />
            <Checkbox
              label="Earn commission on sale orders from assigned clinics"
              checked={editData.salesCommissionRate > 0}
              onChange={(checked) =>
                setEditData((prev) => ({
                  ...prev,
                  salesCommissionRate: checked ? (prev.salesCommissionRate > 0 ? prev.salesCommissionRate : 0.05) : 0,
                }))
              }
            />
            {editData.salesCommissionRate > 0 && (
              <TextField
                label="Sales Commission Rate (%)"
                type="number"
                value={String(editData.salesCommissionRate * 100)}
                onChange={(value) =>
                  setEditData((prev) => ({ ...prev, salesCommissionRate: parseFloat(value) / 100 || 0 }))
                }
                autoComplete="off"
                suffix="%"
                helpText="Flat percentage of the sale order subtotal."
              />
            )}
            <Select
              label="Status"
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ]}
              value={editData.status}
              onChange={(value) => setEditData((prev) => ({ ...prev, status: value }))}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Assign Clinic Modal */}
      <Modal
        open={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        title="Assign to Clinic"
        primaryAction={{
          content: 'Assign',
          onAction: handleAssignClinic,
          disabled: !newAssignment.clinicId,
          loading: assignMutation.isPending,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowAssignModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <SearchableSelect
              label="Clinic"
              value={newAssignment.clinicId}
              selectedLabel={assignClinicName}
              onChange={(value, option) => {
                setNewAssignment((prev) => ({ ...prev, clinicId: value }));
                setAssignClinicName(option?.label ?? '');
              }}
              onSearch={searchUnassignedClinics}
              emptyOption={{ label: 'Select a clinic...', value: '' }}
              placeholder="Type a clinic name…"
            />
            <Select
              label="Assignment Type"
              options={[
                { label: 'Primary', value: '0' },
                { label: 'Secondary', value: '1' },
                { label: 'Split', value: '2' },
              ]}
              value={newAssignment.assignmentType}
              onChange={(value) => setNewAssignment((prev) => ({ ...prev, assignmentType: value }))}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
